import { logError } from '@modernloop/shared/utils';

import { ApplicationStatus, ScheduleTaskColumnType, TaskStatus } from 'src/generated/mloop-graphql';

import { snakeToSentenceCase } from 'src/utils/strings';

import { SheduleTaskDashboardFiltersInterface } from './types';

type ScheduleTaskColumnKeyMapping = {
  [key in ScheduleTaskColumnType]?: string;
};

export const SCHEDULE_TASK_COLUMN_KEYS: ScheduleTaskColumnKeyMapping = {
  [ScheduleTaskColumnType.Candidate]: 'hiring-dashboard-tasks:tasks.column.candidate',
  [ScheduleTaskColumnType.Asignee]: 'hiring-dashboard-tasks:tasks.column.assignee',
  [ScheduleTaskColumnType.Creator]: 'hiring-dashboard-tasks:tasks.column.createdBy',
  [ScheduleTaskColumnType.Job]: 'hiring-dashboard-tasks:tasks.column.job',
  [ScheduleTaskColumnType.JobStage]: 'hiring-dashboard-tasks:tasks.column.stage',
  [ScheduleTaskColumnType.Status]: 'hiring-dashboard-tasks:tasks.column.status',
  [ScheduleTaskColumnType.CreatedAt]: 'hiring-dashboard-tasks:tasks.column.createdDate',
  [ScheduleTaskColumnType.LastActivityAt]: 'hiring-dashboard-tasks:tasks.column.lastActivity',
  [ScheduleTaskColumnType.ScheduleStartAt]: 'hiring-dashboard-tasks:tasks.column.scheduleDate',
  [ScheduleTaskColumnType.ScheduleCreatedAt]: 'hiring-dashboard-tasks:tasks.column.scheduleCreated',
  [ScheduleTaskColumnType.FirstInterviewAt]: 'hiring-dashboard-tasks:tasks.column.firstInterview',
  [ScheduleTaskColumnType.TaskFlags]: 'hiring-dashboard-tasks:tasks.column.flags',
  [ScheduleTaskColumnType.TaskTags]: 'hiring-dashboard-tasks:tasks.column.tags',
  [ScheduleTaskColumnType.Urgent]: 'hiring-dashboard-tasks:tasks.column.urgent',
  [ScheduleTaskColumnType.ApplicationStatus]: 'hiring-dashboard-tasks:tasks.column.applicationState',
  [ScheduleTaskColumnType.Queue]: 'hiring-dashboard-tasks:tasks.column.queue',
  [ScheduleTaskColumnType.CandidateAvailabilityStartAt]:
    'hiring-dashboard-tasks:tasks.column.firstCandidateAvailability',
};

type ScheduleTaskWidthMapping = {
  [key in ScheduleTaskColumnType]?: number;
};

export const SCHEDULE_TASK_COLUMN_WIDTH: ScheduleTaskWidthMapping = {
  [ScheduleTaskColumnType.Candidate]: 292,
  [ScheduleTaskColumnType.Asignee]: 230,
  [ScheduleTaskColumnType.Creator]: 212,
  [ScheduleTaskColumnType.Job]: 212,
  [ScheduleTaskColumnType.JobStage]: 212,
  [ScheduleTaskColumnType.Status]: 212,
  [ScheduleTaskColumnType.CreatedAt]: 96,
  [ScheduleTaskColumnType.LastActivityAt]: 120,
  [ScheduleTaskColumnType.ScheduleStartAt]: 128,
  [ScheduleTaskColumnType.ScheduleCreatedAt]: 152,
  [ScheduleTaskColumnType.FirstInterviewAt]: 128,
  [ScheduleTaskColumnType.TaskFlags]: 252,
  [ScheduleTaskColumnType.TaskTags]: 252,
  [ScheduleTaskColumnType.Urgent]: 120,
  [ScheduleTaskColumnType.ApplicationStatus]: 152,
  [ScheduleTaskColumnType.Queue]: 120,
  [ScheduleTaskColumnType.CandidateAvailabilityStartAt]: 240,
};

export const SCHEDULE_TASK_COLUMN_DISPLAY_SORTABLE = [
  ScheduleTaskColumnType.LastActivityAt,
  ScheduleTaskColumnType.ScheduleCreatedAt,
  ScheduleTaskColumnType.FirstInterviewAt,
  ScheduleTaskColumnType.CandidateAvailabilityStartAt,
];

export const EMPTY_SHEDULE_TASK_DASHBOARD_FILTERS_INTERFACE: SheduleTaskDashboardFiltersInterface = {
  search: '',
  applicationStatuses: [],
  assigneeEmployeeIds: [],
  creatorEmployeeIds: [],
  isUrgent: false,
  showUnassigned: false,
  showCompletedInFlagged: false,
  taskStatuses: [],
  jobIds: [],
  tags: [],
  flags: [],
  jobStageNames: [],
  taskQueueIds: [],
  showNoQueue: false,
  delegateTypes: [],
};

export const isFiltersEmpty = (filters: SheduleTaskDashboardFiltersInterface) => {
  return Boolean(
    filters.search === '' &&
    filters.assigneeEmployeeIds.length === 0 &&
    filters.creatorEmployeeIds.length === 0 &&
    filters.isUrgent === false &&
    filters.taskStatuses.length === 0 &&
    filters.jobIds.length === 0 &&
    filters.tags.length === 0 &&
    filters.flags.length === 0 &&
    filters.jobStageNames.length === 0 &&
    filters.taskQueueIds.length === 0 &&
    filters.showCompletedInFlagged === false &&
    (filters.delegateTypes?.length ?? 0) === 0
  );
};

/**
 * Returns the default task-dashboard columns when an employee has not customized their layout.
 */
export const getScheduleTaskColumns = () => [
  ScheduleTaskColumnType.Candidate,
  ScheduleTaskColumnType.Asignee,
  ScheduleTaskColumnType.Creator,
  ScheduleTaskColumnType.Job,
  ScheduleTaskColumnType.JobStage,
  ScheduleTaskColumnType.Status,
  ScheduleTaskColumnType.CreatedAt,
  ScheduleTaskColumnType.LastActivityAt,
  ScheduleTaskColumnType.ScheduleCreatedAt,
  ScheduleTaskColumnType.FirstInterviewAt,
  ScheduleTaskColumnType.TaskFlags,
  ScheduleTaskColumnType.TaskTags,
];

export const getTaskStatusTranslationKey = (taskStatus: TaskStatus): string => {
  switch (taskStatus) {
    case TaskStatus.Hold:
      return 'hiring-dashboard-tasks:tasks.status.hold';
    case TaskStatus.ReadyToSendToCandidate:
      return 'hiring-dashboard-tasks:tasks.status.readyToSendToCandidate';
    case TaskStatus.NeedsAvailability:
      return 'hiring-dashboard-tasks:tasks.status.needsAvailability';
    case TaskStatus.WaitingForCandidate:
      return 'hiring-dashboard-tasks:tasks.status.waitingForCandidate';
    case TaskStatus.ReadyToSchedule:
      return 'hiring-dashboard-tasks:tasks.status.readyToSchedule';
    case TaskStatus.ReadyToReschedule:
      return 'hiring-dashboard-tasks:tasks.status.readyToReschedule';
    case TaskStatus.Scheduled:
      return 'hiring-dashboard-tasks:tasks.status.scheduled';
    case TaskStatus.Completed:
      return 'hiring-dashboard-tasks:tasks.status.completed';
    case TaskStatus.Canceled:
      return 'hiring-dashboard-tasks:tasks.status.canceled';
    case TaskStatus.NeedsDebrief:
      return 'hiring-dashboard-tasks:tasks.status.needsDebrief';
    default:
      logError(`Unknown TaskStatus "${taskStatus}"`);
      return 'hiring-dashboard-tasks:tasks.status.unknown';
  }
};

export const getDisplayStringForTaskStatus = (taskStatus: TaskStatus) => {
  switch (taskStatus) {
    case TaskStatus.Hold:
      return 'Hold for interviewer RSVPs';

    case TaskStatus.ReadyToSendToCandidate:
      return 'Ready for candidate communications';

    case TaskStatus.NeedsAvailability:
    case TaskStatus.WaitingForCandidate:
    case TaskStatus.ReadyToSchedule:
    case TaskStatus.ReadyToReschedule:
    case TaskStatus.Scheduled:
    case TaskStatus.Completed:
    case TaskStatus.Canceled:
    case TaskStatus.NeedsDebrief:
      return snakeToSentenceCase(taskStatus);

    default:
      logError(`Unknown TaskStatus "${taskStatus}"`);
      return 'Unknown';
  }
};

export const getColorForTaskStatus = (taskStatus: TaskStatus) => {
  switch (taskStatus) {
    case TaskStatus.Hold:
    case TaskStatus.WaitingForCandidate:
    case TaskStatus.ReadyToSendToCandidate:
      return 'warning';

    case TaskStatus.ReadyToSchedule:
    case TaskStatus.ReadyToReschedule:
    case TaskStatus.NeedsDebrief:
      return 'info';

    case TaskStatus.Scheduled:
    case TaskStatus.Completed:
      return 'success';

    case TaskStatus.NeedsAvailability:
    case TaskStatus.Canceled:
      return 'default';

    default:
      logError(`Unknown TaskStatus "${taskStatus}"`);
      return 'default';
  }
};

export const getColorForApplicationStatus = (applicationStatus: ApplicationStatus) => {
  switch (applicationStatus) {
    case ApplicationStatus.Active:
    case ApplicationStatus.Archived:
    case ApplicationStatus.Converted:
      return 'default';

    case ApplicationStatus.Rejected:
      return 'error';

    case ApplicationStatus.Hired:
      return 'success';

    default:
      logError(`Unknown ApplicationStatus "${applicationStatus}"`);
      return 'default';
  }
};

export const getApplicationStatusTranslationKey = (applicationStatus: ApplicationStatus): string => {
  switch (applicationStatus) {
    case ApplicationStatus.Active:
      return 'hiring-dashboard-tasks:tasks.applicationStatus.active';
    case ApplicationStatus.Archived:
      return 'hiring-dashboard-tasks:tasks.applicationStatus.archived';
    case ApplicationStatus.Converted:
      return 'hiring-dashboard-tasks:tasks.applicationStatus.converted';
    case ApplicationStatus.Hired:
      return 'hiring-dashboard-tasks:tasks.applicationStatus.hired';
    case ApplicationStatus.Rejected:
      return 'hiring-dashboard-tasks:tasks.applicationStatus.rejected';
    default:
      logError(`Unknown ApplicationStatus "${applicationStatus}"`);
      return 'hiring-dashboard-tasks:tasks.applicationStatus.unknown';
  }
};
